import {
    areGeoPointsEqual,
    areTimestampsEqual,
    isBytesLike,
    isFirestoreFieldValue,
    isFirestoreReference,
    isGeoPointLike,
    isTimestampLike,
} from './FirestoreUtils';
import { isArrayLike } from './Lang';

export function areArraysEqual<T>(
    xs: ArrayLike<T>,
    ys: ArrayLike<T>,
    compareFn: (x: T, y: T) => boolean
): boolean {
    if (xs === ys) return true;
    if (xs.length !== ys.length) return false;
    for (let i = 0; i < xs.length; i++) {
        if (!compareFn(xs[i], ys[i])) return false;
    }
    return true;
}

function areMappingsEqual<V>(
    a: Record<string, V>,
    b: Record<string, V>,
    compareFn: (a: V, b: V) => boolean
): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    const bKeySet = new Set(bKeys);
    for (const key of aKeys) {
        if (!bKeySet.has(key) || !compareFn(a[key], b[key])) return false;
    }
    return true;
}

function areNumericArraysEqual(
    xs: ArrayLike<number>,
    ys: ArrayLike<number>
): boolean {
    if (xs === ys) return true;
    if (xs.length !== ys.length) return false;
    for (let i = 0; i < xs.length; i++) {
        if (!(xs[i] === ys[i] || (isNaN(xs[i]) && isNaN(ys[i])))) {
            return false;
        }
    }
    return true;
}

// Comparators that detect builtin types based on Object.prototype.toString
interface BuiltinComparator<T> {
    signatures: ReadonlyArray<string>;
    isEqual(a: T, b: T): boolean;
}

function makeBuiltinComp<T>(
    signatures: ReadonlyArray<string>,
    isEqual: (a: T, b: T) => boolean
): BuiltinComparator<T> {
    return { signatures, isEqual };
}

const BUILTIN_COMPARATORS: ReadonlyArray<BuiltinComparator<any>> = [
    makeBuiltinComp<Date>(['Date'], (a, b) => {
        const aTime = a.getTime();
        const bTime = b.getTime();
        return aTime === bTime || (isNaN(aTime) && isNaN(bTime));
    }),
    makeBuiltinComp<RegExp>(
        ['RegExp'],
        (a, b) => a.source === b.source && a.flags === b.flags
    ),
    makeBuiltinComp<ArrayBuffer>(['ArrayBuffer'], (a, b) =>
        areNumericArraysEqual(new Uint8Array(a), new Uint8Array(b))
    ),
    makeBuiltinComp<ArrayLike<number>>(
        [
            'Int8Array',
            'Uint8Array',
            'Uint8ClampedArray',
            'Int16Array',
            'Uint16Array',
            'Int32Array',
            'Uint32Array',
            'Float16Array',
            'Float32Array',
            'Float64Array',
            'BigInt64Array',
            'BigUint64Array',
        ],
        (a, b) => areNumericArraysEqual(a, b)
    ),
    makeBuiltinComp<DataView>(['DataView'], (a, b) =>
        areNumericArraysEqual(
            new Uint8Array(a.buffer, a.byteOffset, a.byteLength),
            new Uint8Array(b.buffer, b.byteOffset, b.byteLength)
        )
    ),
];

// Internal for now, but could eventually facilitate custom comparators
interface PredicateComparator<T> {
    isInstance(x: unknown): x is T;
    isEqual(a: T, b: T): boolean;
}

// This helps us keep the type constraint within the definition,
// even if it just ends up in an array of PredicateComparator<any>
function makePredComp<T>(
    isInstance: (x: unknown) => x is T,
    isEqual: (a: T, b: T) => boolean
): PredicateComparator<T> {
    return { isInstance, isEqual };
}

const CUSTOM_COMPARATORS: ReadonlyArray<PredicateComparator<any>> = [
    makePredComp(isTimestampLike, areTimestampsEqual),
    makePredComp(isGeoPointLike, areGeoPointsEqual),
    makePredComp(isBytesLike, (a, b) => a.isEqual(b)),
    makePredComp(isFirestoreFieldValue, (a, b) => a.isEqual(b)),
    makePredComp(isFirestoreReference, (a, b) => a.path === b.path),
];

function typeOf(a: any) {
    return a === null ? ('null' as const) : typeof a;
}

function getConstructorName(a: any) {
    const aSig = Object.prototype.toString.call(a);
    return aSig.slice(8, -1);
}

function _isDeepEqual(
    a: any,
    b: any,
    seenA: WeakMap<any, any>,
    seenB: WeakMap<any, any>
): boolean {
    if (a === b) return true;

    // Check values with distinct typeofs (including our shim for `null`)
    const valueType = typeOf(a);
    if (valueType !== typeOf(b)) return false;

    switch (valueType) {
        case 'null':
            return true;
        case 'undefined':
            return true;
        case 'boolean':
        case 'string':
        case 'function':
        case 'symbol':
        case 'bigint':
            return a === b;
        case 'number':
            return a === b || (isNaN(a) && isNaN(b));
        // case "object":
        //     /* keep going */;
    }

    // Cycle check
    if (seenA.has(a)) return seenA.get(a) === b;
    if (seenB.has(b)) return seenB.get(b) === a;
    seenA.set(a, b);
    seenB.set(b, a);

    // Check built-in classes with distinct Object.prototype.toString values
    for (const { signatures, isEqual } of BUILTIN_COMPARATORS) {
        const aSig = getConstructorName(a);
        const bSig = getConstructorName(b);
        if (signatures.includes(aSig) || signatures.includes(bSig)) {
            return aSig === bSig && isEqual(a, b);
        }
    }

    for (const { isInstance, isEqual } of CUSTOM_COMPARATORS) {
        const aIsInstance = isInstance(a);
        const bIsInstance = isInstance(b);
        if (aIsInstance || bIsInstance) {
            return aIsInstance && bIsInstance && isEqual(a, b);
        }
    }

    const recurse = (a: any, b: any) => _isDeepEqual(a, b, seenA, seenB);
    if (isArrayLike(a)) {
        return isArrayLike(b) && areArraysEqual(a, b, recurse);
    }
    if (isArrayLike(b)) return false;

    return areMappingsEqual(a, b, recurse);
}

export function isDeepEqual(a: any, b: any): boolean {
    return _isDeepEqual(a, b, new WeakMap(), new WeakMap());
}
